/**
 * Page Prévisionnel de Trésorerie
 * Affiche l'évolution de la trésorerie sur 3 mois glissants
 * avec le détail des encaissements et décaissements
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useTresorerie } from '@/hooks/useTresorerie';
import type { ModeAffichageTresorerie } from '@/types/tresorerie';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MiroAnnotations } from '@/components/annotations/MiroAnnotations';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  TrendingUp,
  TrendingDown,
  Wallet,
  Calendar,
  Settings,
  RefreshCw,
  AlertTriangle,
  Info,
  ChevronRight,
} from 'lucide-react';
import { TresorerieChart } from '@/components/tresorerie/TresorerieChart';
import { TresorerieMoisDetail } from '@/components/tresorerie/TresorerieMoisDetail';
import { TresorerieSettings } from '@/components/tresorerie/TresorerieSettings';
import { TresorerieHistorique } from '@/components/tresorerie/TresorerieHistorique';
import { BulkChargesFixesModal } from '@/components/tresorerie/BulkChargesFixesModal';
import { useChargesFixes } from '@/hooks/useChargesFixes';
import type { CreateChargeFixData } from '@/types/charges-fixes.types';
import { cn } from '@/lib/utils';

/**
 * Formate un montant en euros
 */
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Composant KPI Card avec support onClick pour scroll
 */
function KPICard({
  title,
  value,
  icon: Icon,
  trend,
  trendLabel,
  variant = 'default',
  onClick,
  scrollHint,
}: {
  title: string;
  value: string;
  icon: React.ElementType;
  trend?: number;
  trendLabel?: string;
  variant?: 'default' | 'success' | 'danger' | 'warning';
  onClick?: () => void;
  scrollHint?: string;
}) {
  const variantStyles = {
    default: 'text-foreground',
    success: 'text-green-500',
    danger: 'text-red-500',
    warning: 'text-yellow-500',
  };

  const isClickable = !!onClick;

  return (
    <Card
      className={cn(
        'relative',
        isClickable && 'cursor-pointer transition-all duration-200 hover:ring-2 hover:ring-primary/30 hover:shadow-lg'
      )}
      onClick={onClick}
    >
      <CardContent className="pt-6">
        <div className="flex items-center gap-2 text-muted-foreground mb-1">
          <Icon className="h-4 w-4" />
          <span className="text-sm">{title}</span>
        </div>
        <p className={cn('text-2xl font-bold', variantStyles[variant])}>{value}</p>
        {trendLabel && (
          <p
            className={cn(
              'text-xs mt-1',
              trend && trend >= 0 ? 'text-green-500' : 'text-red-500'
            )}
          >
            {trendLabel}
          </p>
        )}
        {/* Hint de scroll et chevron pour cartes cliquables */}
        {isClickable && scrollHint && (
          <p className="text-xs text-primary/70 mt-2 flex items-center gap-1">
            <ChevronRight className="h-3 w-3" />
            {scrollHint}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Composant Loading Skeleton
 */
function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      {/* KPIs skeleton */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i}>
            <CardContent className="pt-6">
              <Skeleton className="h-4 w-24 mb-2" />
              <Skeleton className="h-8 w-32" />
            </CardContent>
          </Card>
        ))}
      </div>
      {/* Chart skeleton */}
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Page principale Trésorerie
 */
export default function TresoreriePage() {
  const { user } = useAuth();
  const [entrepriseId, setEntrepriseId] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [isFromOnboarding, setIsFromOnboarding] = useState(false);
  const [showBulkCharges, setShowBulkCharges] = useState(false);
  const [isBulkSaving, setIsBulkSaving] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [modeAffichage, setModeAffichage] = useState<ModeAffichageTresorerie>('ttc');
  const [tresorerieInitialisee, setTresorerieInitialisee] = useState<boolean | null>(null);

  // Charger l'entreprise ID
  useEffect(() => {
    const loadEntrepriseId = async () => {
      if (!user?.id) return;

      const { data } = await supabase
        .from('entreprises')
        .select('id, tresorerie_initialisee')
        .eq('proprietaire_user_id', user.id)
        .single();

      if (data) {
        setEntrepriseId(data.id);
        setTresorerieInitialisee((data as any).tresorerie_initialisee ?? false);
      }
    };

    loadEntrepriseId();
  }, [user?.id]);

  // Hook trésorerie
  const { prevision, params, isLoading, error, updateSoldeManuel, refetch } =
    useTresorerie(entrepriseId);

  // Hook charges fixes (pour le modal standalone d'onboarding)
  const { ajouterChargesFixes } = useChargesFixes(entrepriseId);

  // Wrapper pour mettre à jour le flag local après sauvegarde du solde
  const handleUpdateSoldeManuel = useCallback(async (montant: number | null) => {
    await updateSoldeManuel(montant);
    if (montant !== null) {
      setTresorerieInitialisee(true);
    }
  }, [updateSoldeManuel]);

  // Rafraîchir les données
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refetch();
    setIsRefreshing(false);
  };

  // Callback onboarding : l'utilisateur veut définir ses charges fixes après le solde
  const handleRequestChargesFixes = useCallback(() => {
    setShowSettings(false);
    setIsFromOnboarding(false);
    // Petit délai pour laisser le dialog se fermer avant d'ouvrir le suivant
    setTimeout(() => setShowBulkCharges(true), 150);
  }, []);

  // Soumission des charges fixes depuis le modal standalone
  const handleBulkSubmit = useCallback(async (data: CreateChargeFixData[]): Promise<boolean> => {
    setIsBulkSaving(true);
    try {
      return await ajouterChargesFixes(data);
    } finally {
      setIsBulkSaving(false);
    }
  }, [ajouterChargesFixes]);

  // Calculs pour les KPIs (selon mode HT/TTC)
  const isHt = modeAffichage === 'ht';
  const soldeActuel = isHt ? (prevision?.solde_actuel_ht ?? 0) : (prevision?.solde_actuel ?? 0);
  const soldeFinal = isHt ? (prevision?.solde_fin_periode_ht ?? 0) : (prevision?.solde_fin_periode ?? 0);
  const totalEnc3m = isHt ? (prevision?.total_encaissements_3mois_ht ?? 0) : (prevision?.total_encaissements_3mois ?? 0);
  const totalDec3m = isHt ? (prevision?.total_decaissements_3mois_ht ?? 0) : (prevision?.total_decaissements_3mois ?? 0);
  const variation = soldeFinal - soldeActuel;
  const variationPct = soldeActuel !== 0 ? ((variation / Math.abs(soldeActuel)) * 100).toFixed(1) : '0';
  const hasMoisEnDeficit = (prevision?.mois_en_deficit?.length ?? 0) > 0;
  const modeLabel = modeAffichage.toUpperCase();

  // Fonction de scroll vers section avec effet highlight
  const scrollToSection = useCallback((sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      // Scroll vers l'élément
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Ajouter effet highlight temporaire
      element.classList.add('ring-2', 'ring-primary', 'ring-offset-2');
      setTimeout(() => {
        element.classList.remove('ring-2', 'ring-primary', 'ring-offset-2');
      }, 2000);
    }
  }, []);

  // Affichage loading
  if (isLoading && !prevision) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold">Prévisionnel de Trésorerie</h1>
            <p className="text-muted-foreground">Vision à 3 mois glissants</p>
          </div>
        </div>
        <LoadingSkeleton />
      </div>
    );
  }

  // Affichage erreur
  if (error && !prevision) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold">Prévisionnel de Trésorerie</h1>
            <p className="text-muted-foreground">Vision à 3 mois glissants</p>
          </div>
        </div>
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-destructive mb-2">
              <AlertTriangle className="h-5 w-5" />
              <h3 className="font-semibold">Erreur de chargement</h3>
            </div>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={handleRefresh}>Réessayer</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold">Prévisionnel de Trésorerie</h1>
          <p className="text-muted-foreground" data-miro-target="tresorerie-subtitle">Vision à 3 mois glissants</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Toggle HT/TTC */}
          <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
            <button
              onClick={() => setModeAffichage('ht')}
              className={cn(
                "px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
                modeAffichage === 'ht'
                  ? "bg-background shadow-sm text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              HT
            </button>
            <button
              onClick={() => setModeAffichage('ttc')}
              className={cn(
                "px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
                modeAffichage === 'ttc'
                  ? "bg-background shadow-sm text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              TTC
            </button>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw
              className={cn('h-4 w-4 mr-2', isRefreshing && 'animate-spin')}
            />
            Actualiser
          </Button>
          <Button variant="outline" size="sm" onClick={() => { setIsFromOnboarding(false); setShowSettings(true); }} data-miro-target="tresorerie-parametres">
            <Settings className="h-4 w-4 mr-2" />
            Paramètres
          </Button>
        </div>
      </div>

      {/* Overlay de blocage — solde initial non configuré */}
      {tresorerieInitialisee === false && (
        <div className="relative">
          {/* Aperçu flouté en arrière-plan */}
          <div className="pointer-events-none select-none blur-sm opacity-30">
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <Card key={i}>
                  <CardContent className="pt-6">
                    <div className="h-4 w-24 mb-2 bg-muted rounded" />
                    <div className="h-8 w-32 bg-muted rounded" />
                  </CardContent>
                </Card>
              ))}
            </div>
            <Card className="mt-6">
              <CardHeader>
                <div className="h-6 w-48 bg-muted rounded" />
              </CardHeader>
              <CardContent>
                <div className="h-[300px] w-full bg-muted/50 rounded" />
              </CardContent>
            </Card>
          </div>

          {/* Carte centrale overlay */}
          <div className="absolute inset-0 flex items-center justify-center">
            <Card className="max-w-lg mx-4 border-emerald-500/30 bg-[#0F0F14]/98 backdrop-blur-xl shadow-2xl shadow-emerald-500/10">
              <CardContent className="pt-8 pb-8 px-8 text-center">
                <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center mx-auto mb-6 shadow-lg shadow-emerald-500/20">
                  <Wallet className="h-8 w-8 text-white" />
                </div>
                <h2 className="text-xl font-bold text-white mb-3">
                  Configurez votre trésorerie
                </h2>
                <p className="text-white/60 mb-6 text-sm leading-relaxed">
                  Pour garantir la fiabilité de votre prévisionnel, saisissez votre solde bancaire actuel.
                  Cette étape est nécessaire pour utiliser le module trésorerie.
                </p>
                <Button
                  onClick={() => { setIsFromOnboarding(true); setShowSettings(true); }}
                  size="lg"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-8"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Définir mon solde initial
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Contenu principal — visible uniquement après initialisation */}
      {tresorerieInitialisee !== false && (
        <>
          {/* Alerte si mois en déficit */}
          {hasMoisEnDeficit && (
            <Card className="border-yellow-500/50 bg-yellow-500/5">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="h-5 w-5 text-yellow-500 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-yellow-600 dark:text-yellow-400">
                      Attention : trésorerie négative prévue
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Votre trésorerie pourrait passer en négatif sur{' '}
                      {prevision?.mois_en_deficit?.length} mois. Anticipez vos encaissements
                      ou décalez vos décaissements.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* KPIs - cliquables pour scroll vers sections */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <KPICard
              title={`Solde actuel (${modeLabel})`}
              value={formatCurrency(soldeActuel)}
              icon={Wallet}
              trendLabel={
                prevision?.solde_actuel_source === 'manuel'
                  ? 'Saisi manuellement'
                  : 'Calculé automatiquement'
              }
              variant={soldeActuel >= 0 ? 'default' : 'danger'}
              onClick={() => scrollToSection('tresorerie-chart')}
              scrollHint="Voir le graphique"
            />

            <KPICard
              title={`Encaissements prévus (${modeLabel})`}
              value={`+${formatCurrency(totalEnc3m)}`}
              icon={TrendingUp}
              variant="success"
              onClick={() => scrollToSection('detail-mois')}
              scrollHint="Voir le détail"
            />

            <KPICard
              title={`Décaissements prévus (${modeLabel})`}
              value={`-${formatCurrency(totalDec3m)}`}
              icon={TrendingDown}
              variant="danger"
              onClick={() => scrollToSection('detail-mois')}
              scrollHint="Voir le détail"
            />

            <KPICard
              title={`Solde fin de période (${modeLabel})`}
              value={formatCurrency(soldeFinal)}
              icon={Calendar}
              trend={variation}
              trendLabel={`${variation >= 0 ? '+' : ''}${variationPct}% vs actuel`}
              variant={soldeFinal >= 0 ? 'success' : 'danger'}
              onClick={() => scrollToSection('tresorerie-chart')}
              scrollHint="Voir l'évolution"
            />
          </div>

          {/* Graphique */}
          <Card id="tresorerie-chart" className="transition-all duration-300 scroll-mt-6">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Évolution de la trésorerie</CardTitle>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Info className="h-4 w-4" />
                <span>La ligne rouge représente le seuil critique (0 €)</span>
              </div>
            </CardHeader>
            <CardContent>
              {prevision?.mois && prevision.mois.length > 0 ? (
                <TresorerieChart mois={prevision.mois} soldeActuel={soldeActuel} mode={modeAffichage} />
              ) : (
                <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                  Aucune donnée disponible
                </div>
              )}
            </CardContent>
          </Card>

          {/* Détail par mois */}
          {prevision?.mois && prevision.mois.length > 0 && (
            <div id="detail-mois" className="space-y-4 transition-all duration-300 scroll-mt-6" data-miro-target="tresorerie-detail-mois">
              <h2 className="text-xl font-semibold">Détail par mois</h2>
              <Tabs defaultValue={prevision.mois[0]?.mois}>
                <TabsList className="mb-4">
                  {prevision.mois.map((mois) => (
                    <TabsTrigger
                      key={mois.mois}
                      value={mois.mois}
                      className="relative"
                    >
                      {mois.label}
                      {mois.solde_fin < 0 && (
                        <Badge
                          variant="destructive"
                          className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center text-[10px]"
                        >
                          !
                        </Badge>
                      )}
                    </TabsTrigger>
                  ))}
                </TabsList>
                {prevision.mois.map((mois) => (
                  <TabsContent key={mois.mois} value={mois.mois}>
                    <TresorerieMoisDetail mois={mois} mode={modeAffichage} />
                  </TabsContent>
                ))}
              </Tabs>
            </div>
          )}

          {/* Historique de trésorerie */}
          <TresorerieHistorique entrepriseId={entrepriseId} mode={modeAffichage} />
        </>
      )}

      {/* Modal paramètres */}
      <TresorerieSettings
        isOpen={showSettings}
        onClose={() => { setShowSettings(false); setIsFromOnboarding(false); }}
        params={params}
        onUpdate={handleUpdateSoldeManuel}
        soldeCalcule={
          prevision?.solde_actuel_source === 'calcule' ? prevision.solde_actuel : null
        }
        entrepriseId={entrepriseId}
        fromOnboarding={isFromOnboarding}
        onRequestChargesFixes={handleRequestChargesFixes}
      />

      {/* Modal charges fixes standalone (ouvert depuis le flow d'onboarding) */}
      {entrepriseId && (
        <BulkChargesFixesModal
          open={showBulkCharges}
          onOpenChange={setShowBulkCharges}
          entrepriseId={entrepriseId}
          onSubmit={handleBulkSubmit}
          isLoading={isBulkSaving}
        />
      )}

      {/* Annotations Miro Trésorerie */}
      <MiroAnnotations
        pageId="tresorerie"
        annotations={[
          {
            id: "tresorerie-subtitle",
            targetSelector: "[data-miro-target='tresorerie-subtitle']",
            text: "Projection de vos flux financiers sur les 3 prochains mois, mise à jour chaque jour",
            position: "bottom",
            offsetX: 0,
            offsetY: 20,
          },
          {
            id: "tresorerie-settings",
            targetSelector: "[data-miro-target='tresorerie-parametres']",
            text: "Configurez votre solde de départ réel pour des prévisions plus précises",
            position: "bottom",
            offsetX: 0,
            offsetY: 20,
          },
          {
            id: "tresorerie-detail",
            targetSelector: "[data-miro-target='tresorerie-detail-mois']",
            text: "Cliquez sur chaque mois pour voir le détail de vos encaissements et décaissements prévus",
            position: "top",
            offsetX: 0,
            offsetY: -20,
          },
        ]}
      />
    </div>
  );
}
